import { http } from "./http";
export type DossierRule = {
  docType: string;
  required: boolean;
  licenses: string[];
  situations: string[];
};
export type OfficeState = {
  version: number;
  situations: string[];
  assigneeId: string | null;
  status: string;
  reference: string | null;
  submittedOn: string | null;
  blockingDocument: string | null;
  nextDueOn: string | null;
  codeExpiresOn?: string | null;
  nephVerified: boolean;
};
export type OfficeDocument = {
  id: string;
  doc_type: string;
  label: string;
  status: string;
  version: number;
  expires_on: string | null;
  rejection_reason: string | null;
  file_name: string | null;
  provided_at?: string | null;
  created_at?: string;
};
export type OfficeData = {
  candidate: {
    first_name: string;
    last_name: string;
    external_ref: string;
    email: string | null;
  };
  today: string;
  policy: { version: number; rules: DossierRule[] };
  office: OfficeState;
  dossier: {
    status: string;
    blockers: {
      label: string;
      reason: string;
      source: string;
      nextAction: string;
    }[];
    items: {
      key: string;
      /** Administratif, ANTS ou Attestation (catalogue du dossier). */
      category?: string;
      label: string;
      required: boolean;
      source: string;
      reason: string | null;
      nextAction: string | null;
      document: OfficeDocument | null;
    }[];
    /** Pièces rattachées à aucune pièce de la checklist (RIB, titre de séjour…), la plus récente par type. */
    others?: OfficeDocument[];
  };
  nextLesson: {
    id: string;
    startsAt: string;
    endsAt: string;
    instructorName: string;
    instructorUserId: string | null;
    version: number;
  } | null;
  followups: {
    id: string;
    document_id: string;
    task_id: string;
    channel: string;
    body: string;
    state: string;
    due_on: string;
    created_at: string;
  }[];
};
export const officeApi = {
  /** Checklist de l'école (Réglages › Dossier des élèves). */
  getPolicy: async () =>
    (
      await http.get<{
        version: number;
        rules: DossierRule[];
        /** Catalogue des pièces du dossier (clé, catégorie, libellé). */
        documents: { key: string; category: string; label: string }[];
      }>("/v1/candidate-office/policy")
    ).data,
  get: async (id: string) =>
    (await http.get<OfficeData>(`/v1/candidate-office/candidates/${id}`)).data,
  update: async (id: string, body: OfficeState) =>
    http.patch(`/v1/candidate-office/candidates/${id}`, body),
  policy: async (body: { version: number; rules: DossierRule[] }) =>
    http.patch("/v1/candidate-office/policy", body),
  review: async (
    id: string,
    body: {
      version: number;
      status: string;
      expiresOn: string | null;
      reason: string | null;
    },
  ) => http.post(`/v1/candidate-office/documents/${id}/review`, body),
  send: async (id: string) =>
    http.post(`/v1/candidate-office/followups/${id}/send`, { confirmed: true }),
  history: async (id: string, page: number) =>
    (
      await http.get<{
        items: {
          id: string;
          kind: string;
          created_at: string;
          after_data: unknown;
        }[];
        hasMore: boolean;
      }>(`/v1/candidate-office/candidates/${id}/history`, { params: { page } })
    ).data,
  preview: async (url: string) => {
    const tab = window.open('about:blank','_blank');
    if (!tab) throw new Error('Ouverture bloquée par le navigateur.');
    tab.opener = null;
    try {
      const {data}=await http.get(url,{responseType:'blob'});
      const object=URL.createObjectURL(data);
      tab.location.href=object;
      window.setTimeout(()=>URL.revokeObjectURL(object),60000);
    } catch(error) {tab.close();throw error;}
  },
  download: async (url: string, name: string) => {
    const { data } = await http.get(url, { responseType: "blob" });
    const object = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = object;
    a.download = name;
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(object), 1000);
  },
};
